import {makeAutoObservable} from "mobx";
import { IRmaShipment, SHIPMENT_STATUS_ENUM, SHIPMENT_STATUS_TRANSLATION_KEYS_MAP} from "../rma-types";
import RmaPageStore from "./rma-page-store";
import { RETURN_METHOD_TYPE } from "../../../return-methods/return-method-types";
import { SHIPPING_CONNECTION_CODE } from "../../../../models/IntegrationsTypes";

interface ILink {
    label: string | null;
    href: string | null;
}
export interface ILabelOrSlipsLink {
    title: string | null;
    link: ILink;
}

class RmaShipmentStore{
	private _model: IRmaShipment = {} as IRmaShipment;
	
	constructor(private _rmaPageStore: RmaPageStore, model: IRmaShipment) {
		makeAutoObservable(this, {}, { autoBind:true });
        this._setModel(model);
	}
	
    private _setModel(model: IRmaShipment) {
        this._model = ensureValidModel(model);
    }
	
    public get id(): number { return this._model.id; }
	public get status(): typeof SHIPMENT_STATUS_TRANSLATION_KEYS_MAP[keyof typeof SHIPMENT_STATUS_TRANSLATION_KEYS_MAP] { return SHIPMENT_STATUS_TRANSLATION_KEYS_MAP[this._model.status]; }
	public get statusCode(): SHIPMENT_STATUS_ENUM { return this._model.status; }
	public get trackingNumber(): string | null { return this._model.tracking_number; }
	public get trackingUrl(): string | null { return this._model.tracking_url; }
	public get returnLabelUrl(): string | null { return this._model.return_label_url; }
	public get qrImageUrl(): string | null { return this._model.qr_image_url; }
	public get qrRequestJson(): string | null { return this._model.qr_request_json; }
	public get packingSlipUrl(): string | null { return this._model.packing_slip_url; }
	public get isOutboundShipment(): boolean { return this._model.is_outbound_shipment; }
    /* DISCLAIMER: currently, only Happy Returns processing produce rows in Shipment model of type "bag"
        According to PM - we don't need to display bag shipments.
        If we will need to display bag shipments in the future or add additional providers that work with bags,
        we should add additional check here.
        See this Slack Thread for more information:
        https://returngoworkspace.slack.com/archives/C06KACBTNTF/p1725368841247589?thread_ts=1725365470.215369&cid=C06KACBTNTF
    */
    public get isBagShipment(): boolean { return /^HR.*GET-.*/.test(this.trackingNumber ?? ''); }
	public get carrier(): string | null { return this._model.carrier; }
	public get serviceLevel(): string | null { return this._model._service_level; }
    public get aggregatorCode(): SHIPPING_CONNECTION_CODE | null { return this._model._aggregator_code; }

    public get numberOfItems(): number {
        return this._rmaPageStore.customerCaseStore?.productCaseList.filter(pc => pc.shipmentId === this.id).length ?? 0;
    }

    public get returnMethodName(): string|undefined {
        return this._rmaPageStore.rmaReturnMethod?._internal_name;
    }
    public get returnMethodType(): RETURN_METHOD_TYPE|undefined {
        return this._rmaPageStore.rmaReturnMethod?.type;
    }

    private get labelUrl(): string | null {
        if (this.returnLabelUrl && this.packingSlipUrl) {
            return this.packingSlipUrl;
        }

        return this.returnLabelUrl;
    }

    public labelContent(): ILabelOrSlipsLink {
        if (this.isHappyReturns) {
            return {
                title: null,
                link: {
                    label: null,
                    href: null,
                },
            };
        }
        if (this.labelUrl) {
            return {
                title: 'LABEL',
                link: {
                    label: 'VIEW_LABEL',
                    href: this.labelUrl,
                },
            };
        }
        return {
            title: 'LABEL',
            link: {
                label: '--',
                href: null,
            }
        };
    }

    public slipsContent(): ILabelOrSlipsLink {
        if (this.isHappyReturns) {
            if (this.isHappyReturns) {
                return {
                    title: null,
                    link: {
                        label: null,
                        href: null,
                    },
                };
            }
        }
        if (this.packingSlipUrl) {
            return {
                title: 'SLIPS_COLUMN',
                link: {
                    label: 'VIEW',
                    href: this.packingSlipUrl,
                },
            };
        }
        return {
            title: 'SLIPS_COLUMN',
            link: {
                label: '--',
                href: null,
            }
        };
    }

    public get QRImage() {
        // ...in Happy Returns QR is generated inside label_url... so we swap between QA/Label specifically for HR
        if (this.isHappyReturns) {
            const url = this.labelUrl ?? null;
            if (url) {
                return {
                    title: 'VIEW',
                    url: url,
                };
            }
        }
         /** if there is return label QR URL - return relevant object */
         if (this.qrImageUrl) {
            return {
                title: 'VIEW_QR',
                url: this.qrImageUrl,
            };
        }

        /** if there is no return label QR URL, but there is QR information - show object reporting about error */
        if (this.qrRequestJson) {
            return {
                title: 'QR_NOT_GENERATED',
                url: null,
            };
        }

        return {
            title: 'NO_APPROPRIATED_RULE_FOUND',
            url: null,
        };
    }

    private get isHappyReturns() {
        return !this.isOutboundShipment && this.aggregatorCode === SHIPPING_CONNECTION_CODE.HAPPY_RETURNS;
    }

    public get integrationLogoIconUrl(): string | null {
        return this._rmaPageStore.aggregatorsStore?.getLogoIconUrl(this.aggregatorCode ?? undefined) ?? null;
    }
}

function ensureValidModel(model: IRmaShipment){
    return {
        ...model,
        status: Number(model.status),
        is_outbound_shipment: Boolean(model.is_outbound_shipment),
    }
}

export default RmaShipmentStore;